import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

export function AccountManagerAdminDialog({
  open,
  onClose,
  modalPaperSx,
  primary,
  accountManagerModalMode,
  setAccountManagerModalMode,
  accountManagersLoading,
  formatNumber2,
  accountManagers,
  accountManagerDisplayName,
  accountManagerCreateFormOpen,
  setAccountManagerCreateFormOpen,
  accountManagerForm,
  setAccountManagerForm,
  accountManagerCreateSaving,
  createAccountManager,
  ACCOUNT_MANAGER_TIER_OPTIONS,
  accountManagerTierMap,
  normalizeAccountManagerId,
  setAccountManagerTierMap,
  accountManagerTierSaving,
  saveAccountManagerTierAssignments,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
        Account Managers
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
        <Tabs
          value={accountManagerModalMode}
          onChange={(_, value) => setAccountManagerModalMode(value === "assign" ? "assign" : "create")}
          sx={{
            mb: 1.5,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 900 },
            "& .MuiTabs-indicator": { backgroundColor: primary, height: 3, borderRadius: 99 },
          }}
        >
          <Tab value="create" label="Create Manager" />
          <Tab value="assign" label="Assign Manager" />
        </Tabs>

        {accountManagersLoading ? <LinearProgress sx={{ mb: 1.5 }} /> : null}

        {accountManagerModalMode === "create" ? (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Paper sx={{ p: 1.25, borderRadius: 2, border: "1px solid rgba(51,57,153,0.10)", background: "#fff" }}>
              <Typography sx={{ fontWeight: 900, color: "#111827", mb: 0.75 }}>
                Existing Managers ({formatNumber2(accountManagers.length)})
              </Typography>
              {accountManagers.length ? (
                <Box sx={{ display: "grid", gap: 0.75 }}>
                  {accountManagers.map((manager) => (
                    <Box
                      key={`manager-row-${manager.id}`}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        border: "1px solid rgba(226,232,240,0.95)",
                        background: "#f8fafc",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography sx={{ color: "#0f172a", fontWeight: 900 }}>
                        {accountManagerDisplayName(manager)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#475569" }}>
                        {manager.role || "Manager"} • {manager.email || "—"} • {manager.phone || "—"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  No managers created yet.
                </Typography>
              )}
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Button
                onClick={() => setAccountManagerCreateFormOpen((prev) => !prev)}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2.5,
                  fontWeight: 900,
                  border: "1px solid rgba(51,57,153,0.20)",
                  color: primary,
                  background: "#fff",
                  "&:hover": { background: "rgba(99,102,241,0.06)" },
                }}
              >
                {accountManagerCreateFormOpen ? "Hide Form" : "Create New Manager"}
              </Button>
            </Box>

            {accountManagerCreateFormOpen ? (
              <Paper sx={{ p: 1.25, borderRadius: 2, border: "1px solid rgba(51,57,153,0.10)", background: "#fff" }}>
                <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                  <TextField
                    size="small"
                    label="First Name"
                    value={accountManagerForm.firstName}
                    onChange={(e) => setAccountManagerForm((prev) => ({ ...prev, firstName: e.target.value }))}
                    disabled={accountManagerCreateSaving}
                  />
                  <TextField
                    size="small"
                    label="Last Name"
                    value={accountManagerForm.lastName}
                    onChange={(e) => setAccountManagerForm((prev) => ({ ...prev, lastName: e.target.value }))}
                    disabled={accountManagerCreateSaving}
                  />
                  <TextField
                    size="small"
                    label="Email"
                    value={accountManagerForm.email}
                    onChange={(e) => setAccountManagerForm((prev) => ({ ...prev, email: e.target.value }))}
                    disabled={accountManagerCreateSaving}
                  />
                  <TextField
                    size="small"
                    label="Phone Number"
                    value={accountManagerForm.phone}
                    onChange={(e) => setAccountManagerForm((prev) => ({ ...prev, phone: e.target.value }))}
                    disabled={accountManagerCreateSaving}
                  />
                  <TextField
                    size="small"
                    label="Role"
                    value={accountManagerForm.role}
                    onChange={(e) => setAccountManagerForm((prev) => ({ ...prev, role: e.target.value }))}
                    disabled={accountManagerCreateSaving}
                    sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
                  />
                </Box>
                <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    onClick={createAccountManager}
                    disabled={accountManagerCreateSaving}
                    startIcon={accountManagerCreateSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      px: 3,
                      fontWeight: 900,
                      color: "#fff",
                      background: "linear-gradient(90deg,#0ea5e9,#0284c7)",
                      "&:hover": { background: "linear-gradient(90deg,#0284c7,#0369a1)" },
                    }}
                  >
                    {accountManagerCreateSaving ? "Creating..." : "Create Manager"}
                  </Button>
                </Box>
              </Paper>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 1.25 }}>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Select account manager per tier. This updates users so each tier receives the assigned manager.
            </Typography>
            {accountManagers.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Create at least one account manager first.
              </Alert>
            ) : (
              <Paper sx={{ p: 1.25, borderRadius: 2, border: "1px solid rgba(51,57,153,0.10)", background: "#fff" }}>
                <Box sx={{ display: "grid", gap: 1 }}>
                  {ACCOUNT_MANAGER_TIER_OPTIONS.map((tierOpt) => (
                    <Box
                      key={`tier-assignment-${tierOpt.key}`}
                      sx={{
                        display: "grid",
                        gap: 1,
                        alignItems: "center",
                        gridTemplateColumns: { xs: "1fr", md: "140px 1fr" },
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>
                        {tierOpt.label}
                      </Typography>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={String(accountManagerTierMap?.[tierOpt.key] || "")}
                          displayEmpty
                          onChange={(e) => {
                            const nextId = normalizeAccountManagerId(e.target.value);
                            setAccountManagerTierMap((prev) => ({
                              ...prev,
                              [tierOpt.key]: nextId || "",
                            }));
                          }}
                          sx={{ borderRadius: 1.5, backgroundColor: "#fff" }}
                          disabled={accountManagerTierSaving}
                        >
                          <MenuItem value="">
                            <em>Select Account Manager</em>
                          </MenuItem>
                          {accountManagers.map((manager) => (
                            <MenuItem key={`tier-manager-${tierOpt.key}-${manager.id}`} value={manager.id}>
                              {accountManagerDisplayName(manager)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={accountManagerCreateSaving || accountManagerTierSaving}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            border: "1px solid rgba(51,57,153,0.18)",
            color: primary,
            background: "#fff",
            "&:hover": { background: "rgba(99,102,241,0.06)" },
          }}
        >
          Close
        </Button>
        {accountManagerModalMode === "assign" ? (
          <Button
            onClick={saveAccountManagerTierAssignments}
            disabled={accountManagerTierSaving || accountManagersLoading || !accountManagers.length}
            startIcon={accountManagerTierSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 3,
              fontWeight: 900,
              color: "#fff",
              background: "linear-gradient(90deg,#0ea5e9,#0284c7)",
              "&:hover": { background: "linear-gradient(90deg,#0284c7,#0369a1)" },
            }}
          >
            {accountManagerTierSaving ? "Saving..." : "Save Assignments"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export function ConsignorAdminDialog({
  open,
  onClose,
  modalPaperSx,
  primary,
  consignorsLoading,
  formatNumber2,
  consignors,
  consignorCreateFormOpen,
  setConsignorCreateFormOpen,
  consignorForm,
  setConsignorForm,
  normalizeConsignorProfileId,
  consignorCreateSaving,
  createConsignor,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
        Consignors
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
        {consignorsLoading ? <LinearProgress sx={{ mb: 1.5 }} /> : null}

        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Paper sx={{ p: 1.25, borderRadius: 2, border: "1px solid rgba(51,57,153,0.10)", background: "#fff" }}>
            <Typography sx={{ fontWeight: 900, color: "#111827", mb: 0.75 }}>
              Existing Consignors ({formatNumber2(consignors.length)})
            </Typography>
            {consignors.length ? (
              <Box sx={{ display: "grid", gap: 0.75 }}>
                {consignors.map((consignor) => (
                  <Box
                    key={`consignor-row-${consignor.id}`}
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      border: "1px solid rgba(226,232,240,0.95)",
                      background: "#f8fafc",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography sx={{ color: "#0f172a", fontWeight: 900 }}>
                      {consignor.consignorName || consignor.consignorId || consignor.id}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#475569" }}>
                      ID: {consignor.consignorId || consignor.id || "—"} • POC: {consignor.pointOfContact || "—"} • {consignor.email || "—"} • {consignor.phone || "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                No consignors created yet.
              </Typography>
            )}
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              onClick={() => setConsignorCreateFormOpen((prev) => !prev)}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                px: 2.5,
                fontWeight: 900,
                border: "1px solid rgba(51,57,153,0.20)",
                color: primary,
                background: "#fff",
                "&:hover": { background: "rgba(99,102,241,0.06)" },
              }}
            >
              {consignorCreateFormOpen ? "Hide Form" : "Create New Consignor"}
            </Button>
          </Box>

          {consignorCreateFormOpen ? (
            <Paper sx={{ p: 1.25, borderRadius: 2, border: "1px solid rgba(51,57,153,0.10)", background: "#fff" }}>
              <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                <TextField
                  size="small"
                  label="Consignor ID"
                  required
                  value={consignorForm.consignorId}
                  onChange={(e) =>
                    setConsignorForm((prev) => ({
                      ...prev,
                      consignorId: normalizeConsignorProfileId(e.target.value),
                    }))
                  }
                  disabled={consignorCreateSaving}
                />
                <TextField
                  size="small"
                  label="Consignor Name"
                  required
                  value={consignorForm.consignorName}
                  onChange={(e) => setConsignorForm((prev) => ({ ...prev, consignorName: e.target.value }))}
                  disabled={consignorCreateSaving}
                />
                <TextField
                  size="small"
                  label="Point of Contact"
                  value={consignorForm.pointOfContact}
                  onChange={(e) => setConsignorForm((prev) => ({ ...prev, pointOfContact: e.target.value }))}
                  disabled={consignorCreateSaving}
                />
                <TextField
                  size="small"
                  label="Email"
                  value={consignorForm.email}
                  onChange={(e) => setConsignorForm((prev) => ({ ...prev, email: e.target.value }))}
                  disabled={consignorCreateSaving}
                />
                <TextField
                  size="small"
                  label="Phone Number"
                  value={consignorForm.phone}
                  onChange={(e) => setConsignorForm((prev) => ({ ...prev, phone: e.target.value }))}
                  disabled={consignorCreateSaving}
                  sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
                />
              </Box>
              <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={createConsignor}
                  disabled={consignorCreateSaving}
                  startIcon={consignorCreateSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    px: 3,
                    fontWeight: 900,
                    color: "#fff",
                    background: "linear-gradient(90deg,#0ea5e9,#0284c7)",
                    "&:hover": { background: "linear-gradient(90deg,#0284c7,#0369a1)" },
                  }}
                >
                  {consignorCreateSaving ? "Creating..." : "Create Consignor"}
                </Button>
              </Box>
            </Paper>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={consignorCreateSaving}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            border: "1px solid rgba(51,57,153,0.18)",
            color: primary,
            background: "#fff",
            "&:hover": { background: "rgba(99,102,241,0.06)" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
